import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiTarget, FiLayers, FiUser, FiCpu, FiStar, FiBriefcase, FiGlobe, FiHeart, FiAlertCircle, FiTrendingUp, FiAward } from "react-icons/fi";
import ModuleHeader from "../../moduleHeader";
import { getCurrentPackageUserModule, getLatestHumanChart } from "../../../services/api";

const DetailCard = ({ title, subtitle, icon, content, color }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm hover:shadow-md transition-all group"
    >
        <div className="flex items-center gap-5 mb-5">
            <div className={`w-14 h-14 ${color} rounded-[1.25rem] flex items-center justify-center text-2xl shadow-inner group-hover:scale-110 transition-transform`}>
                {icon}
            </div>
            <div className="flex flex-col">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">{title}</span>
                <h5 className="text-xl font-black text-gray-900 tracking-tight leading-none mt-1">{subtitle}</h5>
            </div>
        </div>
        <p className="text-gray-500 text-sm leading-relaxed font-medium">
            {content}
        </p>
    </motion.div>
);

const HumanDesignAnalysis = ({ chartData: propChartData, packageData: propPackageData, embedded = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(!propChartData || !propPackageData);
    const [packageData, setPackageData] = useState(propPackageData || null);
    const [chartData, setChartData] = useState(propChartData || null);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState("detay");

    const TABS = [
        { id: "ozet", label: "Özet" },
        { id: "harita", label: "Harita" },
        { id: "detay", label: "Analiz" }
    ];

    useEffect(() => {
        if (propChartData && propPackageData) {
            setChartData(propChartData);
            setPackageData(propPackageData);
            setLoading(false);
            return;
        }
        const initData = async () => {
            try {
                const pkg = await getCurrentPackageUserModule(id, 13);
                setPackageData(pkg);
                const latest = await getLatestHumanChart();
                if (latest && latest.success && latest.data) {
                    setChartData(latest.data);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        initData();
    }, [id, propChartData, propPackageData]);

    const handleTabClick = (tabId) => {
        if (tabId === "ozet") {
            navigate(`/${id}/13`);
        } else if (tabId === "harita") {
            navigate(`/${id}/13/map`);
        } else {
            setActiveTab(tabId);
        }
    };

    if (loading || !packageData) {
        return (
            <div className="w-full h-screen min-h-screen flex items-center justify-center bg-[#e8e9ed]">
                <div className="flex flex-col items-center justify-center">
                    <div className="relative mb-8">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60" style={{ animationDirection: "reverse", animationDuration: "1.5s" }}></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
                        </div>
                    </div>
                    <p className="text-xl font-semibold text-gray-800 animate-pulse">Yükleniyor...</p>
                </div>
            </div>
        );
    }

    const blueprint = chartData?.energy_blueprint;
    const report = blueprint?.report;

    // Fallback content if API data is missing
    const TYPE_FALLBACKS = {
        'Generator': {
            description: 'Jeneratör olarak, yaşam gücü enerjiniz çok güçlüdür. İç sesinize kulak vererek doğru işlere yanıt verdiğinizde, doyum hissedersiniz.',
            suggestions: [
                'Davet edilmeden harekete geçmeyin',
                'Sakral sesinize güvenin',
                'Sabırlı olun, doğru fırsatlar size gelecek',
                'Evet/hayır sorularıyla karar verin'
            ],
            notSelf: 'Hayal kırıklığı',
            signature: 'Doyum'
        },
        'Manifesting Generator': {
            description: 'Manifesting Jeneratör olarak hem yaratma hem de başlatma gücüne sahipsiniz. Çoklu yeteneklerinizi keşfetmekten korkmayın.',
            suggestions: [
                'Birden fazla tutkunuz olabilir - bu normaldir',
                'Hızlı karar vermekten korkmayın',
                'Atladığınız adımlar için kendinizi suçlamayın',
                'İç sesinizi dinleyin'
            ],
            notSelf: 'Hayal kırıklığı ve Öfke',
            signature: 'Doyum ve Barış'
        },
        'Projector': {
            description: 'Projektör olarak, başkalarının enerjisini yönlendirmek ve rehberlik etmek için tasarlanmışsınız. Tanınmak sizin için çok önemlidir.',
            suggestions: [
                'Davet bekleyin',
                'Kendinizi zorlamayın',
                'Dinlenmeye önem verin',
                'Bilgeliğinizi paylaşın ama zorlamayın'
            ],
            notSelf: 'Acılık',
            signature: 'Başarı'
        },
        'Manifestor': {
            description: 'Manifestör olarak, bağımsız bir şekilde harekete geçme ve başlatma gücüne sahipsiniz. Bilgilendirerek barış bulursunuz.',
            suggestions: [
                'Harekete geçmeden önce bilgilendirin',
                'Bağımsızlığınıza sahip çıkın',
                'İzin istemenize gerek yok',
                'Etkilerinizin farkında olun'
            ],
            notSelf: 'Öfke',
            signature: 'Barış'
        },
        'Reflector': {
            description: 'Reflektör olarak, çevrenizdeki enerjiyi yansıtan benzersiz bir aynasınız. Ay döngüsüne uyum sağlayarak kararlar verirsiniz.',
            suggestions: [
                'Önemli kararlar için 28 gün bekleyin',
                'Çevrenizin kalitesine dikkat edin',
                'Kendinizi başkalarıyla karşılaştırmayın',
                'Yalnızlık zamanına ihtiyacınız var'
            ],
            notSelf: 'Hayal kırıklığı',
            signature: 'Şaşkınlık'
        }
    };

    const typeFallback = TYPE_FALLBACKS[blueprint?.type?.name] || TYPE_FALLBACKS['Generator'];

    const displayData = {
        name: blueprint?.type?.name || "Bilinmiyor",
        description: blueprint?.type?.description || typeFallback.description,
        suggestions: blueprint?.type?.career_suggestions || typeFallback.suggestions,
        signature: blueprint?.signature || typeFallback.signature,
        notSelf: blueprint?.not_self_theme || typeFallback.notSelf,
        strategy: typeof blueprint?.strategy === 'string' ? blueprint.strategy : blueprint?.strategy?.name,
        strategyDesc: blueprint?.strategy?.description || `Bu tip için temel strateji, hayatta daha az direnç ve daha çok akış yaşamanızı sağlar.`,
        authority: blueprint?.authority?.name || "Duygusal",
        authorityDesc: blueprint?.authority?.description || "Otoriteniz, doğru kararlar vermeniz için size rehberlik eden iç pusulanızdır.",
        profile: blueprint?.profile?.name || "5/1",
        profileDesc: blueprint?.profile?.description || "Profiliniz, yaşam amacınızı ve başkalarıyla nasıl etkileşime geçtiğinizi gösterir.",
        specialLife: blueprint?.type?.special_life
    };

    const CENTER_NAMES_TR = {
        Head: 'Kafa',
        Ajna: 'Ajna',
        Throat: 'Boğaz',
        G: 'G Merkezi',
        Ego: 'Ego',
        Sacral: 'Sakral',
        Spleen: 'Dalak',
        Solar: 'Solar P.',
        Root: 'Kök',
        SolarPlexus: 'Solar P.'
    };

    const content = (
        <div className={embedded ? "w-full" : "min-h-screen bg-white w-full px-6 py-4"}>
            {!embedded && (
                <div className="bg-gray-100/60 p-1 rounded-full flex items-center relative gap-1 mb-6">
                    {TABS.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={`relative flex-1 py-2.5 rounded-full text-[10px] font-black transition-all duration-500 ${isActive ? "text-white" : "text-gray-400"}`}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="hdTabAnalysis"
                                        className="absolute inset-0 bg-[#3DA7B5] rounded-full shadow-lg shadow-[#3DA7B5]/20"
                                        transition={{ type: "spring", duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-10">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
            >
                {/* Type Header */}
                <div className="bg-gradient-to-br from-[#3DA7B5] to-[#5ABCC8] p-10 rounded-[3rem] text-white shadow-xl text-center">
                    <span className="text-[10px] font-black text-white/70 uppercase tracking-[0.3em] mb-3 block">TİPİNİZ</span>
                    <h2 className="text-3xl font-black mb-4">{displayData.name}</h2>
                    <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                        {displayData.description}
                    </p>
                </div>

                {/* Signature & Not-Self */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-50 border border-emerald-100 rounded-[2rem] p-6 text-center">
                        <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block mb-2">İMZA</span>
                        <h4 className="text-lg font-black text-emerald-600">{displayData.signature}</h4>
                    </div>
                    <div className="bg-rose-50 border border-rose-100 rounded-[2rem] p-6 text-center">
                        <span className="text-[10px] font-black text-rose-400 uppercase tracking-widest block mb-2">TEMA DEĞİL</span>
                        <h4 className="text-lg font-black text-rose-600">{displayData.notSelf}</h4>
                    </div>
                </div>

                {/* Special Life Section if exists */}
                {displayData.specialLife && (
                    <div className="bg-indigo-50 border border-indigo-100 rounded-[2.5rem] p-8 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-indigo-500 text-xl shadow-sm">
                                <FiStar />
                            </div>
                            <div>
                                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest block">ÖZEL YAŞAM</span>
                                <h4 className="text-lg font-black text-indigo-800">İlişkiler ve Yaşam</h4>
                            </div>
                        </div>
                        <p className="text-sm text-indigo-600/80 leading-relaxed font-medium whitespace-pre-wrap">
                            {displayData.specialLife}
                        </p>
                    </div>
                )}

                {/* Suggestions */}
                <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center text-amber-500 text-xl">
                            <FiBriefcase />
                        </div>
                        <div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">MESLEKLER</span>
                            <h4 className="text-lg font-black text-gray-800">En Doğru Meslekler</h4>
                        </div>
                    </div>
                    <div className="space-y-3">
                        {displayData.suggestions.map((tip, i) => (
                            <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-2xl p-4">
                                <div className="w-6 h-6 bg-[#3DA7B5]/10 rounded-full flex items-center justify-center text-[#3DA7B5] text-xs font-black flex-shrink-0 mt-0.5">
                                    {i + 1}
                                </div>
                                <p className="text-sm text-gray-600 font-medium">{tip}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Detailed Report Sections */}
                {report && (
                    <>
                        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center text-teal-500 text-xl text-center">
                                    <FiUser />
                                </div>
                                <div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">KİŞİLİK</span>
                                    <h4 className="text-lg font-black text-gray-800">Genel Kişilik Analizi</h4>
                                </div>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed font-medium whitespace-pre-wrap">
                                {report.general_personality}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 gap-4">
                            <DetailCard
                                title="HAYAT TEMASI"
                                subtitle={report.life_theme}
                                icon={<FiGlobe />}
                                content="Yaşamınız boyunca ön plana çıkan temel enerji ve tema."
                                color="bg-orange-50"
                            />
                            <DetailCard
                                title="İŞ VE KARİYER"
                                subtitle={report.work_and_career}
                                icon={<FiAward />}
                                content="Profesyonel hayatta sizi başarıya götürecek yaklaşımlar."
                                color="bg-blue-50"
                            />
                            <DetailCard
                                title="İLİŞKİLER"
                                subtitle={report.relationships}
                                icon={<FiHeart />}
                                content="Başkalarıyla kurduğunuz bağlarda dikkat etmeniz gerekenler."
                                color="bg-rose-50"
                            />
                            <DetailCard
                                title="ZORLUKLAR"
                                subtitle={report.core_challenges}
                                icon={<FiAlertCircle />}
                                content="En sık karşılaşabileceğiniz içsel ve dışsal engeller."
                                color="bg-gray-50"
                            />
                            <DetailCard
                                title="GELİŞİM YOLU"
                                subtitle={report.growth_path}
                                icon={<FiTrendingUp />}
                                content="Ruhsal ve kişisel potansiyelinizi gerçekleştirmek için izlemeniz gereken yol."
                                color="bg-emerald-50"
                            />
                        </div>
                    </>
                )}

                {/* Strategy & Authority Cards */}
                <DetailCard
                    title="Strateji"
                    subtitle={displayData.strategy}
                    icon={<FiTarget className="text-purple-500" />}
                    content={displayData.strategyDesc}
                    color="bg-purple-50"
                />

                <DetailCard
                    title="Otorite"
                    subtitle={displayData.authority}
                    icon={<FiCpu className="text-indigo-500" />}
                    content=""
                    color="bg-indigo-50"
                />

                <DetailCard
                    title="Profil"
                    subtitle={displayData.profile}
                    icon={<FiUser className="text-teal-500" />}
                    content={displayData.profileDesc}
                    color="bg-teal-50"
                />

                {/* Centers Summary */}
                <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm pb-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500">
                            <FiLayers size={20} />
                        </div>
                        <div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">MERKEZLER</span>
                            <h4 className="text-lg font-black text-gray-800">Enerji Merkezleriniz</h4>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        {['Head', 'Ajna', 'Throat', 'G', 'Ego', 'Sacral', 'Spleen', 'Solar', 'Root'].map(center => {
                            const isDefined = blueprint?.centers?.defined?.includes(center) || (center === 'Solar' && (blueprint?.centers?.defined?.includes('Solar Plexus') || blueprint?.centers?.defined?.includes('SolarPlexus')));
                            return (
                                <div
                                    key={center}
                                    className={`p-3 rounded-xl text-center ${isDefined ? 'bg-[#3DA7B5]/10 border border-[#3DA7B5]/20' : 'bg-gray-50 border border-gray-100'}`}
                                >
                                    <span className={`text-[10px] font-black ${isDefined ? 'text-[#3DA7B5]' : 'text-gray-400'}`}>
                                        {CENTER_NAMES_TR[center] || center}
                                    </span>
                                    <div className={`text-[9px] mt-1 ${isDefined ? 'text-[#3DA7B5]/70' : 'text-gray-300'} font-bold`}>
                                        {isDefined ? 'Tanımlı' : 'Açık'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </motion.div>
        </div>
    );

    if (embedded) return content;

    return (
        <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
            <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">
                {/* ModuleHeader */}
                {packageData && (
                    <div className="relative z-30 w-full mb-2">
                        <ModuleHeader
                            userName={packageData.user?.first_name + " " + packageData.user?.last_name}
                            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                            title={packageData.package?.name}
                            description={packageData.package?.description}
                            backgroundImage={packageData.package?.image_url}
                            onExpandedChange={setIsHeaderExpanded}
                            showToggle={true}
                            autoHeight={true}
                        />
                    </div>
                )}
                {content}
            </div>
        </div>
    );
};

export default HumanDesignAnalysis;
